<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import Drawer from 'primevue/drawer'
import type { CustomerDbRecord } from '~/types/db/customer'

const { t } = useI18n()
const commerceStore = useCommerceStore()
const { details, hasClientDetails } = storeToRefs(commerceStore)
const { persistDetailsInLocalStorage } = commerceStore

const orderModal = ref(false)
const selectedCustomer = ref<CustomerDbRecord | null>(null)

// Reset or sync picker state when drawer opens
watch(orderModal, (opened) => {
  if (opened) {
    const det = details.value as Record<string, unknown>
    if (det?.id) {
      // Existing customer from previous order — load into picker
      selectedCustomer.value = { ...details.value } as unknown as CustomerDbRecord
    }
    else {
      // No customer stored — start fresh
      selectedCustomer.value = null
    }
  }
})

function onCustomerSelect(customer: CustomerDbRecord) {
  selectedCustomer.value = customer
  details.value = { ...details.value, ...customer }
  persistDetailsInLocalStorage()
}

function onCustomerClear() {
  selectedCustomer.value = null
  commerceStore.clearDetails()
  details.value = { ...commerceStore.initialDetailsState }
}

function onCustomerCreated(customer: CustomerDbRecord) {
  details.value = { ...details.value, ...customer }
  persistDetailsInLocalStorage()
}

function useForOrder() {
  orderModal.value = false
  persistDetailsInLocalStorage()
}

function toggleModal() {
  orderModal.value = !orderModal.value
}

const hasDetails = computed(() => {
  return hasClientDetails.value || selectedCustomer.value !== null
})
</script>

<template>
  <div>
    <Button
      :label="hasDetails ? t('Edit Client') : t('Client')"
      icon="pi pi-user"
      size="small"
      @click="toggleModal"
    />
    <Drawer
      v-model:visible="orderModal"
      position="top"
      :style="{ width: '50rem', height: 'auto' }"
      :header="t('Client details')"
      :pt="{
        header: {
          class: 'bg-gray-300',
        },
      }"
    >
      <div class="mx-auto py-2">
        <SharedCustomerPicker
          v-model="selectedCustomer"
          @select="onCustomerSelect"
          @clear="onCustomerClear"
          @created="onCustomerCreated"
        />

        <div class="flex items-center gap-2 mt-4">
          <Button
            :label="details?.name ? t('Use for this order') : t('Close')"
            icon="pi pi-check"
            severity="success"
            @click="useForOrder"
          />
          <Button
            :label="t('Clear details')"
            icon="pi pi-trash"
            severity="secondary"
            outlined
            @click="onCustomerClear"
          />
        </div>
      </div>
    </Drawer>
  </div>
</template>

<style lang="postcss">
.p-drawer-top .p-drawer {
  height: 100vh;
}
.p-autocomplete-item[data-p-focus='true'] {
  @apply bg-gray-200;
}
</style>
